import {
  DELIVERY_COST_STATUS_LABELS,
  DELIVERY_STATUS_LABELS,
  type Delivery,
  deliveryStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Truck } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { DELIVERY_STATUS_TONE, deliveryTotals } from "../../../../features/fuel/delivery-values.js";
import { useDeliveries, useFuelPermissions } from "../../../../features/fuel/queries.js";
import {
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  useFuelStation,
} from "../../../../features/fuel/shared.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate } from "../../../../lib/format.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...fuelSearchShape,
  status: deliveryStatusSchema.optional().catch(undefined),
  from: date,
  to: date,
});

export const Route = createFileRoute("/_app/akaryakit/dolumlar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: DeliveryListPage,
});

/**
 * Tanker dolumları. Plaka ve şoför adı KİŞİSEL VERİDİR ve listede GÖSTERİLMEZ (KVKK); yalnızca dolum
 * belgesinde ve itiraz tutanağında görünür.
 */
function DeliveryListPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelDeliveries")}
      description={t("fuel.deliveries.list.description")}
      breadcrumb={[{ label: t("menu.fuelDeliveries") }]}
      actions={
        permissions.canManageDeliveries ? (
          <Button asChild>
            <Link to="/akaryakit/dolumlar/yeni">
              <Plus aria-hidden="true" />
              {t("fuel.deliveries.new.title")}
            </Link>
          </Button>
        ) : null
      }
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <DeliveryList companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function DeliveryList({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useFuelStation(companyId);
  const setFilters = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value))) as never,
      replace: true,
    });

  const filters = {
    stationId: station.stationId ?? undefined,
    status: search.status,
    from: search.from,
    to: search.to,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useDeliveries(companyId, filters, pages.cursor);
  const stationNames = useMemo(
    () => new Map(station.stations.map((item) => [item.id, `${item.code} ${item.name}`])),
    [station.stations],
  );

  const columns = useMemo<DataTableColumn<Delivery>[]>(
    () => [
      {
        id: "deliveryNo",
        header: t("fuel.deliveries.fields.deliveryNo"),
        hideable: false,
        exportValue: (row) => row.deliveryNo,
        cell: (row) => (
          <Link
            to="/akaryakit/dolumlar/$id"
            params={{ id: row.id }}
            className="font-mono underline underline-offset-4"
          >
            {row.deliveryNo}
          </Link>
        ),
      },
      {
        id: "businessDate",
        header: t("fuel.deliveries.fields.businessDate"),
        exportValue: (row) => row.businessDate,
        cell: (row) => formatDate(row.businessDate),
      },
      {
        id: "station",
        header: t("fuel.station"),
        exportValue: (row) => stationNames.get(row.stationId) ?? null,
        cell: (row) => stationNames.get(row.stationId) ?? "—",
      },
      {
        id: "distributor",
        header: t("fuel.deliveries.fields.distributor"),
        exportValue: (row) => row.distributorName,
        cell: (row) => row.distributorName,
      },
      {
        id: "despatchNo",
        header: t("fuel.deliveries.fields.despatchNo"),
        exportValue: (row) => row.despatchNo,
        cell: (row) => row.despatchNo ?? "—",
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => DELIVERY_STATUS_LABELS[row.status],
        cell: (row) => (
          <StatusBadge tone={DELIVERY_STATUS_TONE[row.status]}>
            {DELIVERY_STATUS_LABELS[row.status]}
          </StatusBadge>
        ),
      },
      {
        id: "documentL",
        header: t("fuel.deliveries.fields.documentL"),
        align: "right",
        exportValue: (row) => deliveryTotals(row.lines).documentL.toString(),
        cell: (row) => `${deliveryTotals(row.lines).documentL.formatTr(3)} L`,
      },
      {
        id: "measuredL",
        header: t("fuel.deliveries.fields.measuredL"),
        align: "right",
        exportValue: (row) => deliveryTotals(row.lines).measuredL?.toString() ?? null,
        cell: (row) => {
          const measured = deliveryTotals(row.lines).measuredL;
          return measured === null ? "—" : `${measured.formatTr(3)} L`;
        },
      },
      {
        id: "differenceL",
        header: t("fuel.deliveries.fields.differenceL"),
        align: "right",
        exportValue: (row) => deliveryTotals(row.lines).differenceL?.toString() ?? null,
        cell: (row) => {
          const difference = deliveryTotals(row.lines).differenceL;
          if (difference === null) return "—";
          return (
            <span className="tabular">
              {`${difference.formatTr(3)} L`}
              {row.withinTolerance === false ? (
                <>
                  {" "}
                  <StatusBadge tone="danger">{t("fuel.deliveries.tolerance.outside")}</StatusBadge>
                </>
              ) : null}
            </span>
          );
        },
      },
      {
        id: "costStatus",
        header: t("fuel.deliveries.fields.costStatus"),
        exportValue: (row) => DELIVERY_COST_STATUS_LABELS[row.costStatus],
        cell: (row) =>
          row.costStatus === "NONE" ? (
            "—"
          ) : (
            <StatusBadge tone={row.costStatus === "FINAL" ? "success" : "warning"}>
              {DELIVERY_COST_STATUS_LABELS[row.costStatus]}
            </StatusBadge>
          ),
      },
    ],
    [t, stationNames],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <DataTable
      caption={t("fuel.deliveries.list.caption")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Truck />}
          title={t("fuel.deliveries.list.emptyTitle")}
          description={t("fuel.deliveries.list.emptyDescription")}
        />
      }
      filters={
        <>
          <StationField selection={station} />
          <FormField label={t("common.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) =>
                  setFilters({
                    status:
                      event.target.value === "" ? undefined : deliveryStatusSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {deliveryStatusSchema.options.map((status) => (
                  <option key={status} value={status}>
                    {DELIVERY_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("fuel.deliveries.list.from")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={search.from ?? null}
                onValueChange={(value) => setFilters({ from: value ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("fuel.deliveries.list.to")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={search.to ?? null}
                onValueChange={(value) => setFilters({ to: value ?? undefined })}
              />
            )}
          </FormField>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
